import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  SwipeableDrawer,
  Typography,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import StoreMallDirectoryRoundedIcon from '@mui/icons-material/StoreMallDirectoryRounded';
import EditLocationAltRoundedIcon from '@mui/icons-material/EditLocationAltRounded';
import ThumbUpRoundedIcon from '@mui/icons-material/ThumbUpRounded';
import ThumbDownRoundedIcon from '@mui/icons-material/ThumbDownRounded';
import { useTranslation } from 'react-i18next';
import { loadPresentation, placeLabel } from '../../domain/places/presentation';
import { getOpeningStatusText, normalizeOpeningPeriods } from '../../utils/openingHours';
import OpeningHoursDialog from './OpeningHoursDialog';
import { getContainedPlaces, getPlace, setPlacePopularityVote } from '../../services/api';
import PlaceEditDialog from './PlaceEditDialog';

const styles = {
  sheet: {
    right: 0,
    bottom: 0,
    left: 0,
    mx: 'auto',
    width: { xs: '100%', sm: 'min(760px, calc(100vw - 32px))' },
    minHeight: 100,
    maxHeight: { xs: '72dvh', sm: '82dvh' },
    p: { xs: '30px 16px max(16px, env(safe-area-inset-bottom))', sm: '32px' },
    border: '1px solid',
    borderColor: 'divider',
    borderBottom: 0,
    borderRadius: '28px 28px 0 0',
    bgcolor: 'background.paper',
    boxShadow: '0 -20px 65px rgba(17,39,35,.2)',
    backdropFilter: 'blur(20px)',
    overflowX: 'hidden',
    overflowY: 'auto',
    overscrollBehavior: 'contain',
    WebkitOverflowScrolling: 'touch',
  },
  handle: {
    position: 'absolute', top: 11, right: '50%', height: 5,
    borderRadius: 99, bgcolor: 'text.disabled', transform: 'translateX(50%)',
  },
  close: {
    position: 'absolute',
    top: { xs: 15, sm: 19 },
    right: { xs: 14, sm: 21 },
    width: 39,
    height: 39,
    bgcolor: 'action.hover',
    color: 'text.secondary',
  },
  loading: {
    minHeight: 230, color: 'text.secondary', fontWeight: 800,
    alignItems: 'center', justifyContent: 'center', flexDirection: 'row', gap: 1.5,
  },
  eyebrow: { m: '0 0 7px', color: 'primary.main', fontSize: 12, fontWeight: 900 },
  title: {
    pr: { xs: 10, sm: 11 },
    fontSize: { xs: 24, sm: 26 },
    lineHeight: 1.1,
    fontWeight: 500,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  address: { mt: 1, mb: 0, color: 'text.secondary' },
  contained: {
    mt: 2.4, px: 2, py: 1.5, borderRadius: '13px',
    color: 'info.dark', bgcolor: 'action.hover',
  },
  facts: {
    display: 'grid',
    gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
    gap: { xs: 1, sm: 1.25 }, mt: { xs: 2, sm: 2.6 },
    '& > .MuiPaper-root': {
      display: 'grid', gap: 0.7, minHeight: { xs: 78, sm: 84 }, p: { xs: 1.25, sm: 1.75 },
      borderRadius: '16px', bgcolor: 'action.hover',
    },
    '& span': { color: 'text.secondary', fontSize: 12 },
    '& strong': { fontSize: 17 },
  },
  viewOnly: { mt: 2.1, mb: 0, color: 'text.secondary', textAlign: 'center', fontSize: 12 },
};

export default function PlaceSheet({ place, loading, onClose, onPlaceSelect, onFloorSelect }) {
  const { t, i18n } = useTranslation();
  const [showOpeningHours, setShowOpeningHours] = useState(false);
  const [loadingContainedPlaces, setLoadingContainedPlaces] = useState(false);
  const [showPlaceEdit, setShowPlaceEdit] = useState(false);
  const [popularityVote, setPopularityVote] = useState(null);
  const [popularityCount, setPopularityCount] = useState(0);
  const [savingPopularityVote, setSavingPopularityVote] = useState(false);
  const load = loadPresentation(place, t);
  const wait = place?.load?.estimatedWaitMinutes ?? place?.estimatedWaitTime;
  const trend = place?.load?.trend;
  const basedOn = place?.load?.basedOn;
  const hasFieldSource = Number(basedOn?.userReports || 0) > 0 ||
    Number(basedOn?.gpsReports || 0) > 0;
  const hasAiSource = Number(basedOn?.aiReports || 0) > 0;
  const hasConfidence = Number(place?.load?.confidence || 0) > 0;
  const source = hasConfidence && hasFieldSource
    ? t('place.sourceField')
    : hasConfidence && hasAiSource
      ? t('place.sourceAi')
      : t('load.unknown');
  const statusLabel = hasFieldSource
    ? t('place.fieldStatus')
    : hasAiSource
      ? t('place.estimatedStatus')
      : t('place.status');
  const trendValue = !trend || trend === 'unknown'
    ? t('load.unknown')
    : trend === 'up'
      ? t('place.rising')
      : trend === 'down'
        ? t('place.falling')
        : t('place.stable');
  const openingStatusText = getOpeningStatusText(place?.openingHours, t);
  const hasOpeningHours = normalizeOpeningPeriods(place?.openingHours).length > 0;

  useEffect(() => {
    setShowOpeningHours(false);
    setLoadingContainedPlaces(false);
    setPopularityVote(place?.popularityVote ?? null);
    setPopularityCount(Number(place?.popularityCount) || 0);
  }, [place?.id, place?.popularityCount, place?.popularityVote]);

  const changePopularityVote = async (nextVote) => {
    if (!place?.id || savingPopularityVote) return;
    const requestedVote = popularityVote === nextVote ? 0 : nextVote;
    setSavingPopularityVote(true);
    try {
      const result = await setPlacePopularityVote(place.id, requestedVote);
      setPopularityVote(result.popularityVote ?? null);
      setPopularityCount(Number(result.popularityCount) || 0);
    } finally {
      setSavingPopularityVote(false);
    }
  };
  const openContainedPlaces = async () => {
    if (!place?.id || loadingContainedPlaces) return;
    setLoadingContainedPlaces(true);
    try {
      const children = await getContainedPlaces(place.id);
      const grouped = new Map();
      (Array.isArray(children) ? children : []).forEach((child) => {
        const floor = String(child?.containment?.floor ?? '').trim();
        const key = floor || '__unknown__';
        if (!grouped.has(key)) grouped.set(key, { floor, places: [] });
        grouped.get(key).places.push(child);
      });
      const floors = [...grouped.values()].sort((a, b) => {
        if (!a.floor) return 1;
        if (!b.floor) return -1;
        return a.floor.localeCompare(b.floor, undefined, { numeric: true });
      });
      if (floors.length) {
        onFloorSelect({
          container: place,
          floor: floors[0].floor,
          places: floors[0].places,
          floors,
          preserveViewport: true,
        });
      }
    } finally {
      setLoadingContainedPlaces(false);
    }
  };

  const openParentPlace = async () => {
    const parentId = place?.containment?.parentPlaceId;
    if (!parentId) return;
    try {
      const parent = await getPlace(parentId);
      if (parent) onPlaceSelect(parent);
    } catch {
      // Keep the current place open when its parent cannot be loaded.
    }
  };

  console.log("place", place);
  return (
    <>
      <SwipeableDrawer
        anchor="bottom"
        open={Boolean(place || loading)}
        onClose={onClose}
        onOpen={() => { }}
        disableSwipeToOpen
        swipeAreaWidth={32}
        ModalProps={{ keepMounted: true, hideBackdrop: true }}
        PaperProps={{ component: 'section', elevation: 12, 'data-place-sheet': 'true' }}
        sx={{
          pointerEvents: 'none',
          '& .MuiDrawer-paper': { ...styles.sheet, pointerEvents: 'auto' },
        }}
      >
        <Box sx={styles.handle} />
        <IconButton sx={styles.close} onClick={onClose} aria-label={t('place.close')}>
          <CloseRoundedIcon />
        </IconButton>

        {!!place && (
          <IconButton
            onClick={() => setShowPlaceEdit(true)}
            aria-label={t('placeEdit.openDialog')}
            title={t('placeEdit.openDialog')}
            sx={{ ...styles.close, right: { xs: 58, sm: 68 } }}
          >
            <EditLocationAltRoundedIcon />

          </IconButton>
        )}

        {loading && !place ? (
          <Stack sx={styles.loading}>
            <CircularProgress size={26} />
            {t('place.loading')}
          </Stack>
        ) : (
          <>
            <Stack>
              <Typography component="h1" sx={styles.title}>{placeLabel(place)}</Typography>
              <Typography sx={styles.address}>
                {place?.address
                  ? `${place?.address} ${place?.city ? `, ${place?.city}` : ""} ${place?.city ? `, ${place?.country}` : ""}`
                  : t('place.addressUnavailable')
                }
              </Typography>
            </Stack>

            {!!openingStatusText && (
              <Button
                onClick={() => hasOpeningHours && setShowOpeningHours(true)}
                aria-label={hasOpeningHours ? t('openingHours.showAll') : undefined}
                disabled={!hasOpeningHours}
                startIcon={<AccessTimeRoundedIcon />}
                endIcon={i18n.dir() === 'rtl'
                  ? <ChevronLeftRoundedIcon />
                  : <ChevronRightRoundedIcon />}
                sx={{
                  mt: 1.2, px: 0.5, py: 0.6, width: 'fit-content',
                  color: 'text.secondary', justifyContent: 'flex-start',
                  fontSize: 14, fontWeight: 600,
                  '& .MuiButton-startIcon': { color: 'text.secondary' },
                  '& .MuiButton-endIcon': { color: 'text.secondary' },
                  '&.Mui-disabled': { color: 'text.secondary' },
                }}
              >
                {openingStatusText}
              </Button>
            )}

            {place?.placeKind === 'unit' && place?.containment?.parentName && (
              <Button
                onClick={openParentPlace}
                disabled={!place?.containment?.parentPlaceId}
                startIcon={<StoreMallDirectoryRoundedIcon />}
                endIcon={place?.containment?.parentPlaceId && (
                  i18n.dir() === 'rtl' ? <ChevronLeftRoundedIcon /> : <ChevronRightRoundedIcon />
                )}
                sx={{
                  ...styles.contained, width: '100%', justifyContent: 'flex-start',
                  color: 'text.secondary', gap: 0.7,
                }}
              >
                {t('containedPlaces.inside', { name: place.containment.parentName })}
              </Button>
            )}

            {place?.placeKind === 'container' && (
              <Button
                onClick={openContainedPlaces}
                disabled={loadingContainedPlaces}
                startIcon={<StorefrontRoundedIcon />}
                endIcon={i18n.dir() === 'rtl'
                  ? <ChevronLeftRoundedIcon />
                  : <ChevronRightRoundedIcon />}
                sx={{
                  mt: 1.5, px: 1.5, py: 1.2, width: '100%',
                  justifyContent: 'flex-start', gap: 0.7,
                  border: '1px solid', borderColor: 'divider',
                  borderRadius: '14px', color: 'text.primary',
                  bgcolor: 'action.hover', fontWeight: 800,
                }}
              >
                {loadingContainedPlaces ? <CircularProgress size={20} /> : t('containedPlaces.open')}
              </Button>
            )}

            <Stack sx={styles.facts}>
              <Paper component={Stack} elevation={0}>
                <Typography component="span">{statusLabel}</Typography>
                <Typography component="strong" fontWeight={800} sx={{ color: load.color }}>{load.label}</Typography>
              </Paper>
              <Paper component={Stack} elevation={0}>
                <Typography component="span">{t('place.wait')}</Typography>
                <Typography component="strong" fontWeight={800}>
                  {Number.isFinite(Number(wait)) ? t('place.approximately', { count: wait }) : '---'}
                </Typography>
              </Paper>
              <Paper component={Stack} elevation={0}>
                <Typography component="span">{t('place.trend')}</Typography>
                <Typography component="strong" fontWeight={800}>{trendValue}</Typography>
              </Paper>
              <Paper component={Stack} elevation={0}>
                <Typography component="span">{t('place.source')}</Typography>
                <Typography component="strong" fontWeight={800}>{source}</Typography>
              </Paper>
            </Stack>
          </>
        )}
      </SwipeableDrawer>
      <OpeningHoursDialog
        open={showOpeningHours}
        openingHours={place?.openingHours}
        onClose={() => setShowOpeningHours(false)}
      />
      <PlaceEditDialog
        open={showPlaceEdit}
        place={place}
        onClose={() => setShowPlaceEdit(false)}
      />
    </>
  );
}
